import { Input } from '@shared/components/ui';
import { Bell, Clock, AlertTriangle, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AlertSettingsProps {
  data: {
    alert_settings?: {
      grace_period_minutes?: number;
      throttle_interval_minutes?: number;
    };
  };
  onChange: (data: unknown) => void;
  onNext?: () => void;
  onBack?: () => void;
}

export function AlertSettings({
  data,
  onChange,
  onNext: _onNext,
  onBack: _onBack,
}: AlertSettingsProps) {
  const { t } = useTranslation('backups');
  const [errors, setErrors] = useState<{
    grace_period_minutes?: string;
    throttle_interval_minutes?: string;
  }>({});

  const settings = data.alert_settings || {
    grace_period_minutes: 10,
    throttle_interval_minutes: 60,
  };

  const updateSetting = (key: string, value: number | undefined) => {
    onChange({
      ...data,
      alert_settings: {
        ...settings,
        [key]:
          value === undefined || isNaN(value) ? (key === 'grace_period_minutes' ? 10 : 60) : value,
      },
    });
    setErrors({ ...errors, [key]: undefined });
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-2xl flex items-start space-x-3">
        <Info className="text-state-warn-light dark:text-state-warn-dark shrink-0" size={20} />
        <div className="text-xs font-medium text-state-warn-light dark:text-state-warn-dark leading-relaxed">
          <p className="mb-2">{t('wizard.alerts.infoTitle')}</p>
          <ul className="list-disc list-inside space-y-1 text-state-warn-light dark:text-state-warn-dark">
            <li>
              <strong>{t('wizard.alerts.gracePeriodLabel')}</strong>{' '}
              {t('wizard.alerts.gracePeriodDesc')}
            </li>
            <li>
              <strong>{t('wizard.alerts.throttleLabel')}</strong> {t('wizard.alerts.throttleDesc')}
            </li>
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl">
          <div className="flex items-center space-x-2 mb-3">
            <Clock className="text-state-warn-light dark:text-state-warn-dark" size={18} />
            <label
              htmlFor="backup-alert-grace-period"
              className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark"
            >
              {t('wizard.alerts.gracePeriodInput')}
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Input
              id="backup-alert-grace-period"
              type="number"
              min={1}
              max={1440}
              placeholder="10"
              value={settings.grace_period_minutes || ''}
              onChange={(e) => updateSetting('grace_period_minutes', parseInt(e.target.value))}
              error={errors.grace_period_minutes}
              className="text-center"
            />
            <span className="text-xs text-text-muted-light dark:text-text-muted-dark whitespace-nowrap">
              {t('wizard.alerts.minutes')}
            </span>
          </div>
          <p className="mt-2 text-xs text-text-muted-light dark:text-text-muted-dark">
            {t('wizard.alerts.gracePeriodHelp')}
          </p>
        </div>

        <div className="p-4 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl">
          <div className="flex items-center space-x-2 mb-3">
            <AlertTriangle
              className="text-state-danger-light dark:text-state-danger-dark"
              size={18}
            />
            <label
              htmlFor="backup-alert-throttle-interval"
              className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark"
            >
              {t('wizard.alerts.throttleInput')}
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Input
              id="backup-alert-throttle-interval"
              type="number"
              min={5}
              max={1440}
              placeholder="60"
              value={settings.throttle_interval_minutes || ''}
              onChange={(e) => updateSetting('throttle_interval_minutes', parseInt(e.target.value))}
              error={errors.throttle_interval_minutes}
              className="text-center"
            />
            <span className="text-xs text-text-muted-light dark:text-text-muted-dark whitespace-nowrap">
              {t('wizard.alerts.minutes')}
            </span>
          </div>
          <p className="mt-2 text-xs text-text-muted-light dark:text-text-muted-dark">
            {t('wizard.alerts.throttleHelp')}
          </p>
        </div>
      </div>

      <div className="p-4 bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 rounded-xl">
        <div className="flex items-start space-x-2">
          <Bell className="text-state-ok-light dark:text-state-ok-dark shrink-0 mt-0.5" size={16} />
          <div className="text-xs font-medium text-state-ok-light dark:text-state-ok-dark">
            <p className="font-bold mb-1">{t('wizard.alerts.howItWorks')}</p>
            <ol className="list-decimal list-inside space-y-1 text-state-ok-light dark:text-state-ok-dark">
              <li>{t('wizard.alerts.steps.0')}</li>
              <li>{t('wizard.alerts.steps.1')}</li>
              <li>{t('wizard.alerts.steps.2')}</li>
              <li>{t('wizard.alerts.steps.3')}</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
